import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import type { SelectionExtension } from '@atlaskit/editor-plugin-selection-extension';
import PlusSquareIcon from '@atlaskit/icon/core/plus-square';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ISSUE_CREATE_SIDE_PANEL_ID,
	useIssueCreateSidePanelContext,
} from '@confluence/issue-create-side-panel';
import { useContentType, usePageContentId } from '@confluence/page-context';
import {
	useRightSidebarContext,
	type RightSidebarContextType,
} from '@confluence/page-layout-context';
import type { PageMode } from '@confluence/issue-create-side-panel';
import { fg } from '@confluence/feature-gating';

import { messages } from '../messages';
import { jiraWorkItemCreateActions } from '../jiraWorkCreateActions';

export type UseJiraWorkCreateSelectionExtensionProps = {
	isSidePanelOpenRef: React.MutableRefObject<boolean>;
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
};

const WorkItemCreationType = {
	SINGLE_WORK_ITEM: 'SINGLE_ISSUE',
	MULTIPLE_WORK_ITEM: 'MULTIPLE_ISSUE',
	AI_WORK_ITEM: 'AI_ISSUE',
} as const;

type WorkItemCreationType = (typeof WorkItemCreationType)[keyof typeof WorkItemCreationType];

export const useJiraWorkCreateSelectionExtension = ({
	isSidePanelOpenRef,
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
}: UseJiraWorkCreateSelectionExtensionProps): SelectionExtension => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	// TODO: Remove this when we remove confluence_disable_jira_button_if_side_panel_open
	const rightSidebarContext = useRightSidebarContext();
	const [, actions] = useIssueCreateSidePanelContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireCreateWorkItemButtonClickedAnalytics = useCallback(
		(workItemCreationType: WorkItemCreationType, isCreatedFromTable: boolean) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'jiraIssueLink',
					type: 'ui',
					attributes: {
						issueCreationType: workItemCreationType,
						// Set to false because a dropdown or flyout is not implemented at this entry point.
						// TODO: Update this value if a dropdown or flyout is added in the future.
						isDropdownItem: false,
						isCreatedFromTable,
						pageMode: pageModeRef.current,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, pageModeRef],
	);

	const handleSingleCreateClick = useCallback(async () => {
		actions.setHasEditPermission(hasEditPermissionRef.current);
		const activeSelection = window.getSelection();
		const container = document.querySelector('div#ak-editor-textarea');

		fireCreateWorkItemButtonClickedAnalytics(
			WorkItemCreationType.SINGLE_WORK_ITEM,
			// Set to false because bulk creation from the table is not yet available at this entry point.
			// TODO: Update this value if bulk creation functionality is added in the future.
			false,
		);

		// TODO: Remove isSidePanelOpenRef when we remove confluence_disable_jira_button_if_side_panel_open
		if (isSidePanelOpenRef.current && !fg('confluence_disable_jira_button_if_side_panel_open')) {
			const callRightSidebarClose = async () => rightSidebarContext.closeAll();
			// Need to make sure the sidebar is closed before creating a new work item
			await callRightSidebarClose();
		}
		jiraWorkItemCreateActions({
			activeSelection,
			container,
			contentId,
			contentType,
			rightSidebarContext: fg('confluence_disable_jira_button_if_side_panel_open')
				? rightSidebarContextRef?.current
				: rightSidebarContext,
			actions,
			pageMode: pageModeRef.current,
		});
	}, [
		fireCreateWorkItemButtonClickedAnalytics,
		isSidePanelOpenRef,
		contentId,
		contentType,
		rightSidebarContextRef,
		actions,
		pageModeRef,
		rightSidebarContext,
		hasEditPermissionRef,
	]);

	return {
		name: formatMessage(messages.singleCreateWorkItemName),
		icon: PlusSquareIcon,
		onClick: () => {
			void handleSingleCreateClick();
		},
		isDisabled: () => {
			if (fg('confluence_disable_jira_button_if_side_panel_open')) {
				const currentSidebar = rightSidebarContextRef?.current?.getCurrent();
				return currentSidebar?.panelId === ISSUE_CREATE_SIDE_PANEL_ID;
			}
			return false;
		},
	};
};
